import { DialogFooter, IDialogContentProps } from '@fluentui/react';

import AppContext from 'App/AppContext';
import { IPDefaultButton, IPDialog, IPPrimaryButton, IPScrollablePane, IPText } from 'baseComponents';
import { IPDialogType } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import WarningMessage from 'components/Notification/WarningMessage';
import { onRenderLabelWithInfo } from 'globalFunctions';
import { globalStackTokensGapMedium } from 'globalStyles';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';

export interface IDialogConfirmDeleteBulkProps {
  totalCount: number;
  authCount: number;
}

export interface IDialogConfirmDeleteProps {
  onYes: () => void;
  onNo: () => void;
  hidden: boolean;
  title: string;
  subText: string;
  confirmText?: string;
  optionalJSX?: JSX.Element;
  featureType?: FeatureTypes;
  bulkOptions?: IDialogConfirmDeleteBulkProps;
}

const DialogConfirmDelete = (props: IDialogConfirmDeleteProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const defConfirmText = t('translation:Dialogs.ConfirmDelete.DefaultConfirmDeleteText');
  const [yesDisabled, setYesDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (!props.hidden && props.bulkOptions) {
      if (props.bulkOptions.authCount === 0) {
        setYesDisabled(true);
      }
    }
  }, [props.bulkOptions, props.hidden]);

  const getConfirmText = (): string => {
    return props.confirmText ?? defConfirmText;
  };

  const [enteredConfirmText, setEnteredConfirmText] = useState<string>(
    hasUserFeature(appContext, FeatureTypes.TenantAdmin) ? getConfirmText() : '',
  );

  const dialogContentProps: IDialogContentProps = {
    type: IPDialogType.normal,
    title: props.title,
    showCloseButton: true,
    onDismiss: () => dismiss(),
  };

  const isValidConfirmText = (): boolean => {
    return enteredConfirmText.toLowerCase() === getConfirmText().toLowerCase();
  };

  const hasFeature = (): boolean => {
    if (!props.featureType) return true;

    if (!appContext.user.login.userLicensed || !hasUserFeature(appContext, props.featureType)) {
      return false;
    }

    return true;
  };

  const confirm = () => {
    setEnteredConfirmText('');
    props.onYes();
  };

  const dismiss = () => {
    setEnteredConfirmText('');
    props.onNo();
  };

  if (!hasFeature()) {
    return (
      <IPDialog hidden={props.hidden} onDismiss={props.onNo} dialogContentProps={dialogContentProps}>
        <IPStack verticalFill>
          <IPStack.Item>
            <WarningMessage message={t('translation:Dialogs.ConfirmDelete.NoRole')} />
          </IPStack.Item>
        </IPStack>
        <DialogFooter>
          <IPDefaultButton onClick={() => dismiss()} text={t('translation:Dialogs.Ok.Ok')} />
        </DialogFooter>
      </IPDialog>
    );
  }

  return (
    <IPDialog hidden={props.hidden} dialogContentProps={dialogContentProps}>
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <WarningMessage message={t('translation:Dialogs.ConfirmDelete.Warning')} />
        </IPStack.Item>
        {props.bulkOptions && (
          <IPStack.Item>
            <IPText>
              {t('translation:Dialogs.ConfirmDelete.Bulk', {
                total: props.bulkOptions.totalCount,
                auth: props.bulkOptions.authCount,
              })}
            </IPText>
          </IPStack.Item>
        )}
        {props.optionalJSX && (
          <IPStack.Item
            grow
            styles={{ root: { marginTop: 0, position: 'relative', minHeight: 100, maxHeight: '20vh' } }}
          >
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{props.optionalJSX}</IPScrollablePane>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <IPTextField
            autoFocus={!appContext.isMobileView}
            onPaste={(e) => {
              e.preventDefault();

              return false;
            }}
            value={enteredConfirmText}
            onRenderLabel={() =>
              onRenderLabelWithInfo(
                t('translation:Dialogs.ConfirmDelete.ConfirmTextLabel'),
                t('translation:Dialogs.ConfirmDelete.ConfirmTextLabelInfo', {
                  confirmText: getConfirmText(),
                }),
              )
            }
            placeholder={t('translation:Dialogs.ConfirmDelete.ConfirmTextPlaceHolder', {
              confirmText: getConfirmText(),
            })}
            onChange={(ev, newValue) => setEnteredConfirmText(newValue ?? '')}
          ></IPTextField>
        </IPStack.Item>
        <IPStack.Item>
          <IPText>{props.subText}</IPText>
        </IPStack.Item>
      </IPStack>
      <DialogFooter>
        <IPPrimaryButton
          disabled={!isValidConfirmText() || yesDisabled}
          onClick={() => confirm()}
          text={t('translation:Dialogs.YesNo.Yes')}
        />
        <IPDefaultButton onClick={() => dismiss()} text={t('translation:Dialogs.YesNo.No')} />
      </DialogFooter>
    </IPDialog>
  );
};

export default DialogConfirmDelete;
